import {
  Time,
  type DateDuration,
  type DateTimeDuration,
  type TimeDuration,
} from "@internationalized/date";
import { type z } from "zod";

import { type SearchPassengers } from "@trip/constants";
import { type activityCancelPolicySchema } from "@trip/linktivity";

import { type TourConfig } from "./tours";

/**
 * For the fields `days` and `seconds` in the cancellation policy, their interpretation varies based
 * on the `timing_type`:
 * - If `timing_type` is `ParticipantDay` or `BookingDay`, the `days` field represents the number of
 *   days before or after the activity or booking date, respectively. In this case, the `seconds`
 *   field indicates a specific fixed time of day (e.g., 7200 seconds represents 2:00 AM).
 * - If `timing_type` is `ParticipantTime` or `BookingTime`, both `days` and `seconds` together form
 *   an offset. `days` gives the number of days before or after the start of the activity or booking
 *   time, while `seconds` provides an additional offset in terms of hours, minutes, and seconds
 *   from midnight of the base date. To derive the exact deadline, one needs the reference
 *   time—either the start time of the activity or the time the booking was made.
 */

type LinktivityCancellationPolicy = z.infer<typeof activityCancelPolicySchema>;

type PolicyTime =
  | {
      duration: Omit<DateTimeDuration, "days"> & { days: number };
      type: "time";
    }
  | { days: number; time: Time; type: "day" };

/*
 * "x days and n hr m minutes before/after the participant/booking time (ParticipantTime, BookingTime)""
 * vs .
 * x days before/after (TimeOfDay local time) the participant/booking date (ParticipantDay, BookingDay)
 */
function getPolicyDurationOrTime(
  policy: LinktivityCancellationPolicy,
): null | PolicyTime {
  if (
    typeof policy.days !== "number" ||
    policy.charge_type === "NotCancellable"
  ) {
    return null;
  }

  const days = policy.days ?? 0;

  const dateDuration: DateDuration = { days };
  const timeDuration: TimeDuration = {
    hours: Math.floor((policy.seconds ?? 0) / 3600),
    minutes: Math.floor(((policy.seconds ?? 0) % 3600) / 60),
    seconds: (policy.seconds ?? 0) % 60,
  };

  switch (policy.timing_type) {
    case "ParticipantDay":
    case "BookingDay":
      return {
        type: "day" as const,
        days: policy.days,
        time: new Time(timeDuration.hours, timeDuration.minutes),
      };

    case "ParticipantTime":
    case "BookingTime":
      return {
        type: "time" as const,
        duration: {
          ...dateDuration,
          ...timeDuration,
          days: policy.days,
        },
      };

    default:
      return null;
  }
}

function durationToHours(duration: TimeDuration): number {
  let totalHours = 0;

  if (duration.hours) {
    totalHours += duration.hours;
  }

  if (duration.minutes) {
    totalHours += duration.minutes / 60;
  }

  // Round to two decimal places
  return Math.round(totalHours * 100) / 100;
}

export function explainLinktivityCancellationPolicy(
  policy: LinktivityCancellationPolicy,
) {
  const durationOrTime = getPolicyDurationOrTime(policy);

  if (
    !policy.fee ||
    policy.charge_type === "NotCancellable" ||
    !durationOrTime ||
    (policy.charge_type === "PercentPerBooking" && policy.fee === "100%")
  ) {
    return "The booking cannot be canceled or changed.";
  }

  let timing: string;

  if (durationOrTime.type === "day") {
    const timeString =
      durationOrTime.days === 1 ? "1 day" : `${durationOrTime.days} days`;
    timing = `if you cancel ${timeString} before the activity before ${durationOrTime.time.toString()} in the local time of the activity`;
  } else {
    timing = `if you cancel ${durationToHours(
      durationOrTime.duration,
    )} hours in advance of the activity`;
  }

  switch (policy.charge_type) {
    case "FixedPerParticipant":
      return `A cancellation fee of ${policy.fee} per participant applies ${timing}.`;
    case "FixedPerBooking":
      return `A cancellation fee of ${policy.fee} applies ${timing}.`;
    case "PercentPerBooking":
      return `A cancellation fee of ${policy.fee} of the total booking amount applies ${timing}.`;
  }
}

export function getLinktivityPricingCategoryKey(type: string) {
  switch (type) {
    case "AGE_ADULT":
      return "adults";
    case "AGE_CHILD":
      return "children";
    case "AGE_INFANT":
      return "infants";
    case "AGE_YOUTH":
      return "youth";
    case "AGE_SENIOR":
      return "seniors";
    case "AGE_STUDENT":
      return "students";
    case "AGE_ADULT_AND_CHILD":
      return "adultAndChild";
    case "AGE_DISABLED_ADULT":
      return "disabledAdult";
    case "AGE_HIGH_SCHOOL":
      return "highSchool";
    case "AGE_INTERMEDIATE_SCHOOL":
      return "intermediateSchool";
    case "AGE_ELEMENTALY_SCHOOL":
      return "elementarySchool";
    case "GROUP_VAR":
      return "group";
    default:
      return null;
  }
}

export function getLinktivityPassengerCategoriesFromTripPassengers(
  tripPassengers: SearchPassengers,
  passengerCategories: TourConfig["passengerCategories"],
) {
  let remainingChildren = [...tripPassengers.children];

  function getPassengerCountForCategory(
    category: (typeof passengerCategories)[0],
  ) {
    if (
      category.type === "AGE_ADULT" ||
      category.type === "AGE_ADULT_AND_CHILD"
    ) {
      return tripPassengers.adults;
    }

    if (category.type === "AGE_INFANT") {
      const count = remainingChildren.filter(({ age }) => age < 3).length;
      remainingChildren = remainingChildren.filter(({ age }) => age >= 3);
      return count;
    }

    if (category.type === "AGE_CHILD") {
      const minAge = passengerCategories.some((c) => c.type === "AGE_INFANT")
        ? 3
        : 0;
      const maxAge = passengerCategories.some(
        (c) => c.type === "AGE_HIGH_SCHOOL",
      )
        ? 13
        : 18;
      const count = remainingChildren.filter(
        ({ age }) => age >= minAge && age < maxAge,
      ).length;
      remainingChildren = remainingChildren.filter(({ age }) => age >= maxAge);
      return count;
    }

    if (category.type === "AGE_HIGH_SCHOOL") {
      const count = remainingChildren.filter(({ age }) => age >= 13).length;
      return count;
    }

    return 0;
  }

  return passengerCategories.map((category) => {
    const count = getPassengerCountForCategory(category);
    return {
      count,
      id: category.id,
    };
  });
}
